import React, { useRef, useEffect, useState } from 'react';
import './HistoryTimeline.css';

interface HistoryEvent {
  id: string;
  year: number;
  category: string;
  description: string;
  significance: number;
  tags: string[];
}

interface HistoryTimelineProps {
  events: HistoryEvent[];
  startYear: number;
  endYear: number;
  selectedYear: number | null;
  onYearSelect: (year: number | null) => void;
  onEventClick: (event: HistoryEvent) => void;
}

export const HistoryTimeline: React.FC<HistoryTimelineProps> = ({
  events,
  startYear,
  endYear,
  selectedYear,
  onYearSelect,
  onEventClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredEvent, setHoveredEvent] = useState<HistoryEvent | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  const totalYears = endYear - startYear;
  const significantEvents = events.filter(e => e.significance >= 6);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    drawTimeline(ctx, rect.width, rect.height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, selectedYear, startYear, endYear]);

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      battle: '#ff6b6b',
      discovered: '#4ecdc4',
      founded: '#45b7d1',
      destroyed: '#e74c3c',
      cataclysm: '#9b59b6',
      cultural: '#f39c12',
      religious: '#3498db',
      political: '#2ecc71',
      economic: '#1abc9c',
    };
    return colors[category] || '#95a5a6';
  };

  const drawTimeline = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.clearRect(0, 0, width, height);

    const padding = { top: 40, bottom: 60, left: 60, right: 60 };
    const timelineWidth = width - padding.left - padding.right;

    // Draw timeline base
    ctx.strokeStyle = '#34495e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding.left, height / 2);
    ctx.lineTo(width - padding.right, height / 2);
    ctx.stroke();

    // Draw year markers
    const yearStep = Math.max(50, Math.floor(totalYears / 10));
    ctx.fillStyle = '#7f8c8d';
    ctx.font = '12px "Sometype Mono", monospace';
    ctx.textAlign = 'center';

    for (let year = startYear; year <= endYear; year += yearStep) {
      const x = padding.left + ((year - startYear) / totalYears) * timelineWidth;
      
      ctx.beginPath();
      ctx.moveTo(x, height / 2 - 5);
      ctx.lineTo(x, height / 2 + 5);
      ctx.stroke();

      ctx.fillText(year.toString(), x, height / 2 + 25);
    }

    // Draw events
    significantEvents.forEach(event => {
      const x = padding.left + ((event.year - startYear) / totalYears) * timelineWidth;
      const isAbove = event.year % 2 === 0;
      const y = isAbove ? height / 2 - 40 : height / 2 + 40;
      
      const color = getCategoryColor(event.category);
      const radius = 4 + (event.significance - 6) * 2;

      // Draw event marker
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();

      // Draw connecting line to timeline
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, height / 2);
      ctx.lineTo(x, isAbove ? y + radius : y - radius);
      ctx.stroke();

      // Highlight selected year
      if (selectedYear === event.year) {
        ctx.strokeStyle = '#f1c40f';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, radius + 4, 0, Math.PI * 2);
        ctx.stroke();
      }
    });

    // Draw current year indicator
    if (selectedYear) {
      const x = padding.left + ((selectedYear - startYear) / totalYears) * timelineWidth;
      ctx.strokeStyle = '#f1c40f';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, height - padding.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = { top: 40, bottom: 60, left: 60, right: 60 };
    const timelineWidth = rect.width - padding.left - padding.right;

    // Check if clicking on an event
    significantEvents.forEach(event => {
      const eventX = padding.left + ((event.year - startYear) / totalYears) * timelineWidth;
      const isAbove = event.year % 2 === 0;
      const eventY = isAbove ? rect.height / 2 - 40 : rect.height / 2 + 40;
      const radius = 4 + (event.significance - 6) * 2;

      const distance = Math.sqrt((x - eventX) ** 2 + (y - eventY) ** 2);
      if (distance <= radius + 5) {
        onEventClick(event);
        onYearSelect(event.year);
      }
    });

    // Check if clicking on timeline to select year
    if (y >= rect.height / 2 - 10 && y <= rect.height / 2 + 10) {
      const year = Math.round(startYear + ((x - padding.left) / timelineWidth) * totalYears);
      onYearSelect(Math.max(startYear, Math.min(endYear, year)));
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = { top: 40, bottom: 60, left: 60, right: 60 };
    const timelineWidth = rect.width - padding.left - padding.right;

    let foundEvent: HistoryEvent | null = null;

    significantEvents.forEach(event => {
      const eventX = padding.left + ((event.year - startYear) / totalYears) * timelineWidth;
      const isAbove = event.year % 2 === 0;
      const eventY = isAbove ? rect.height / 2 - 40 : rect.height / 2 + 40;
      const radius = 4 + (event.significance - 6) * 2;

      const distance = Math.sqrt((x - eventX) ** 2 + (y - eventY) ** 2);
      if (distance <= radius + 5) {
        foundEvent = event;
      }
    });

    setHoveredEvent(foundEvent);
    setTooltipPosition(foundEvent ? { x: e.clientX, y: e.clientY } : null);
  };

  return (
    <div className="history-timeline">
      <div className="timeline-header">
        <h3>Interactive Timeline</h3>
        <p className="timeline-subtitle">Click on events to explore • Drag to navigate timeline</p>
      </div>
      <div className="timeline-container">
        <canvas
          ref={canvasRef}
          className="timeline-canvas"
          onClick={handleCanvasClick}
          onMouseMove={handleCanvasMouseMove}
          onMouseLeave={() => {
            setHoveredEvent(null);
            setTooltipPosition(null);
          }}
        />
        {hoveredEvent && tooltipPosition && (
          <div
            className="timeline-tooltip"
            style={{ left: tooltipPosition.x + 10, top: tooltipPosition.y - 10 }}
          >
            <div className="tooltip-year">Year {hoveredEvent.year}</div>
            <div className="tooltip-category">{hoveredEvent.category}</div>
            <div className="tooltip-description">{hoveredEvent.description}</div>
            <div className="tooltip-significance">★ {hoveredEvent.significance}/10</div>
          </div>
        )}
      </div>
      <div className="timeline-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#ff6b6b' }}></span>
          <span>Battle</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#4ecdc4' }}></span>
          <span>Discovery</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#e74c3c' }}></span>
          <span>Destroyed</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#9b59b6' }}></span>
          <span>Cataclysm</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#f39c12' }}></span>
          <span>Cultural</span>
        </div>
      </div>
    </div>
  );
};
